// Downloads a linked video (F-01.13, D-44, D-45): a moonland generation or upload, a shared Google Drive file, or a
// YouTube video through the local helper (yt-dlp on this computer, docs/helper.md). These GET / POST requests are
// the application's only network traffic besides its own files; no video or picture data is sent.
import { HELPER_ORIGIN, parseLink, remoteFileName, remoteUrl, remoteUrls, type RemoteRef } from '../core/remote-media';

export class RemoteDownloadError extends Error {
  constructor(
    readonly errorKey: string,
    readonly detail: string,
  ) {
    super(detail);
  }
}

/** Container type from the first bytes (Drive often answers application/octet-stream). */
function sniffType(head: Uint8Array, declared: string): string {
  const ascii = (a: number, b: number) => String.fromCharCode(...head.subarray(a, b));
  if (ascii(4, 8) === 'ftyp') return ascii(8, 12) === 'qt  ' ? 'video/quicktime' : 'video/mp4';
  if (head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3) return declared === 'video/webm' ? 'video/webm' : 'video/x-matroska';
  return declared;
}

/** Reads a response body, reporting the fraction done when the size is known. */
async function readBody(res: Response, onProgress?: (done: number) => void): Promise<Uint8Array[]> {
  const total = Number(res.headers.get('content-length')) || 0;
  const reader = res.body!.getReader();
  const chunks: Uint8Array[] = [];
  let got = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    got += value.byteLength;
    if (total) onProgress?.(Math.min(1, got / total));
  }
  return chunks;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    });
  });
}

/** A request to the helper; a failed connection means it is not running. */
async function helper(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(HELPER_ORIGIN + path, { ...init, credentials: 'omit' });
  } catch (e) {
    if ((e as DOMException).name === 'AbortError') throw e;
    throw new RemoteDownloadError('error.remote.helper', String(e));
  }
}

/** YouTube: the helper downloads with yt-dlp (progress polled), then hands over the finished MP4. */
async function downloadYoutube(ref: RemoteRef, onProgress?: (done: number) => void, signal?: AbortSignal): Promise<File> {
  const start = await helper(new URL(remoteUrl(ref)).pathname + new URL(remoteUrl(ref)).search, { method: 'POST', signal });
  if (!start.ok) throw new RemoteDownloadError('error.remote.helper', `HTTP ${start.status}`);
  const { job } = (await start.json()) as { job: string };
  for (;;) {
    const st = (await (await helper(`/v1/jobs/${job}`, { signal })).json()) as { state: string; progress: number; error?: string };
    if (st.state === 'done') break;
    if (st.state === 'error') throw new RemoteDownloadError(st.error === 'ytdlp-missing' ? 'error.remote.ytdlp' : 'error.remote.youtube', st.error ?? '');
    onProgress?.(st.progress * 0.95);
    await sleep(500, signal);
  }
  const res = await helper(`/v1/jobs/${job}/file`, { signal });
  if (!res.ok || !res.body) throw new RemoteDownloadError('error.remote.helper', `HTTP ${res.status}`);
  const chunks = await readBody(res, (p) => onProgress?.(0.95 + p * 0.05));
  return new File(chunks as BlobPart[], remoteFileName(ref), { type: 'video/mp4', lastModified: Date.now() });
}

/** Fetches the video into memory; `onProgress` gets the fraction done (0–1) when the size is known. */
export async function downloadRemote(ref: RemoteRef, onProgress?: (done: number) => void, signal?: AbortSignal): Promise<File> {
  if (ref.kind === 'youtube') return downloadYoutube(ref, onProgress, signal);
  let res: Response | null = null;
  let found = ref;
  // moonland keeps generations in a folder per model maker and names some videos output.mp4: the next likely
  // address is tried when one is not found (D-44: still only moonland's storage).
  for (const url of remoteUrls(ref)) {
    try {
      res = await fetch(url, { credentials: 'omit', signal });
    } catch (e) {
      if ((e as DOMException).name === 'AbortError') throw e;
      throw new RemoteDownloadError('error.remote.network', String(e));
    }
    if (res.status !== 404) {
      // The folder it was found in is kept in the file name, so reopening goes straight there.
      const r = ref.kind === 'moonland' ? parseLink(url) : null;
      if (r?.ok) found = r.ref;
      break;
    }
    await res.body?.cancel();
  }
  if (!res) throw new RemoteDownloadError(`error.remote.notFound.${ref.kind}`, 'no address');
  if (res.status === 404 || res.status === 403 || res.status === 401) throw new RemoteDownloadError(`error.remote.notFound.${ref.kind}`, `HTTP ${res.status}`);
  if (!res.ok || !res.body) throw new RemoteDownloadError('error.remote.network', `HTTP ${res.status}`);
  const declared = (res.headers.get('content-type') ?? '').split(';')[0].trim().toLowerCase();
  // An HTML page instead of the file: the Drive file is not shared publicly, or its download quota is used up.
  if (declared.startsWith('text/')) throw new RemoteDownloadError(`error.remote.notFound.${ref.kind}`, declared);
  const chunks = await readBody(res, onProgress);
  const head = chunks[0]?.subarray(0, 16) ?? new Uint8Array();
  const type = sniffType(head, declared);
  if (!type.startsWith('video/') && type !== 'application/octet-stream') throw new RemoteDownloadError('error.remote.notVideo', type);
  return new File(chunks as BlobPart[], remoteFileName(found, type), { type: type.startsWith('video/') ? type : 'video/mp4', lastModified: Date.now() });
}

/**
 * F-01.14: a video the browser cannot decode (ProRes, DNxHR…) is sent to the local helper on this computer
 * (127.0.0.1, never elsewhere), converted to H.264 with ffmpeg and returned. `onProgress`: 0–0.1 upload,
 * 0.1–0.95 conversion, then the download of the result.
 */
export async function transcodeWithHelper(file: File, onProgress?: (done: number) => void, signal?: AbortSignal): Promise<File> {
  const name = file.name.replace(/\.[^.]+$/, '');
  onProgress?.(0.02);
  const start = await helper(`/v1/transcode?name=${encodeURIComponent(name)}`, { method: 'POST', body: file, signal });
  if (start.status === 404) throw new RemoteDownloadError('error.transcode.helperOld', 'HTTP 404');
  if (!start.ok) throw new RemoteDownloadError('error.remote.helper', `HTTP ${start.status}`);
  const { job } = (await start.json()) as { job: string };
  for (;;) {
    const st = (await (await helper(`/v1/jobs/${job}`, { signal })).json()) as { state: string; progress: number; error?: string };
    if (st.state === 'done') break;
    if (st.state === 'error') throw new RemoteDownloadError(st.error === 'ffmpeg-missing' ? 'error.transcode.ffmpeg' : 'error.transcode.failed', st.error ?? '');
    onProgress?.(0.1 + st.progress * 0.85);
    await sleep(500, signal);
  }
  const res = await helper(`/v1/jobs/${job}/file`, { signal });
  if (!res.ok || !res.body) throw new RemoteDownloadError('error.remote.helper', `HTTP ${res.status}`);
  const chunks = await readBody(res, (p) => onProgress?.(0.95 + p * 0.05));
  return new File(chunks as BlobPart[], `${name}_h264.mp4`, { type: 'video/mp4', lastModified: Date.now() });
}
